import { useEffect, type ReactNode } from 'react';
import { View } from 'react-native';

import { prefetchPageDocument } from '@/lib/blocks/api';
import type { BlockRecord } from '@/lib/blocks/types';
import {
  pageHasChildPagesInTree,
  prefetchSidebarPageChildLists,
  type EnsureSidebarChildren,
} from '@/lib/layout/sidebar-child-pages';
import { NAV_SECTION_IDS, type NavSection } from '@/lib/layout/sidebar-nav';
import type { FavoritePage } from '@/lib/favorites/types';
import type { RecentPage } from '@/lib/navigation/recent-pages';

import { SidebarItem } from './SidebarItem';
import { SidebarEmptyHint } from './SidebarEmptyHint';

type SidebarNavSectionsProps = {
  navSections: NavSection[];
  recentPages: RecentPage[];
  favoritePages: FavoritePage[];
  childrenByParent: Record<string, BlockRecord[]>;
  expanded: Record<string, boolean>;
  activePageId: string | null;
  toggleExpanded: (id: string, defaultExpanded?: boolean) => void;
  onEnsureChildren?: EnsureSidebarChildren;
  onOpenPage: (id: string, type: 'page' | 'database') => void;
  onNewSubpageAtRoot?: (rootBlockId: string) => void;
  onFavoriteDuplicate?: (page: FavoritePage) => void;
  onFavoriteRename?: (page: FavoritePage) => void;
  onFavoriteTrash?: (page: FavoritePage) => void;
  onRecentTrash?: (page: RecentPage) => void;
  renderWorkspaceTree: (rootBlockId: string) => ReactNode;
};

function NavSectionHeader({
  label,
  isOpen,
  isActive,
  headerToggleOnly,
  onToggle,
  onOpen,
  onNewSubpage,
  onPrefetch,
}: {
  label: string;
  isOpen: boolean;
  isActive: boolean;
  headerToggleOnly: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onNewSubpage?: () => void;
  onPrefetch?: () => void;
}) {
  return (
    <SidebarItem
      canDuplicate={false}
      canRename={false}
      canTrash={false}
      depth={0}
      hasChildPages
      headerToggleOnly={headerToggleOnly}
      isActive={isActive}
      isExpanded={isOpen}
      label={label}
      onDuplicate={() => {}}
      onNewSubpage={onNewSubpage}
      onPrefetch={onPrefetch}
      onPress={onOpen}
      onRename={() => {}}
      onToggleExpand={onToggle}
      onTrash={() => {}}
      variant="section"
    />
  );
}

function NavEmptyHint({ message }: { message: string }) {
  return <SidebarEmptyHint message={message} />;
}

export function SidebarNavSections({
  navSections,
  recentPages,
  favoritePages = [],
  childrenByParent,
  expanded,
  activePageId,
  toggleExpanded,
  onEnsureChildren,
  onOpenPage,
  onNewSubpageAtRoot,
  onFavoriteDuplicate,
  onFavoriteRename,
  onFavoriteTrash,
  onRecentTrash,
  renderWorkspaceTree,
}: SidebarNavSectionsProps) {
  const recentsOpen = expanded[NAV_SECTION_IDS.recents] ?? true;
  const favoritesOpen = expanded[NAV_SECTION_IDS.favorites] ?? true;

  useEffect(() => {
    if (!recentsOpen || !onEnsureChildren) {
      return;
    }
    prefetchSidebarPageChildLists(recentPages, childrenByParent, onEnsureChildren);
  }, [recentsOpen, recentPages, childrenByParent, onEnsureChildren]);

  useEffect(() => {
    if (!favoritesOpen || !onEnsureChildren) {
      return;
    }
    prefetchSidebarPageChildLists(favoritePages, childrenByParent, onEnsureChildren);
  }, [favoritesOpen, favoritePages, childrenByParent, onEnsureChildren]);

  return (
    <>
      {navSections.map((section) => {
        const isOpen = expanded[section.id] ?? true;

        if (section.kind === 'recents') {
          return (
            <View key={section.id} className="mb-1">
              <NavSectionHeader
                headerToggleOnly
                isActive={false}
                isOpen={isOpen}
                label={section.label}
                onOpen={() => toggleExpanded(section.id, true)}
                onToggle={() => toggleExpanded(section.id, true)}
              />
              {isOpen ? (
                <View>
                  {recentPages.length === 0 ? (
                    <NavEmptyHint message="Pages you open will appear here." />
                  ) : (
                    recentPages.map((page) => {
                      const childList = childrenByParent[page.id];
                      const hasChildPages = pageHasChildPagesInTree(
                        page.id,
                        page.type,
                        childrenByParent,
                      );
                      return (
                        <SidebarItem
                          key={page.id}
                          blockId={page.id}
                          depth={1}
                          hasChildPages={hasChildPages}
                          isActive={activePageId === page.id}
                          label={page.title}
                          leadingIcon={page.type === 'database' ? 'database' : 'page'}
                          onDuplicate={() => {}}
                          onPress={() => onOpenPage(page.id, page.type)}
                          onPrefetch={
                            page.type === 'page' ? () => prefetchPageDocument(page.id) : undefined
                          }
                          onPrefetchChildren={
                            page.type === 'page' && childList === undefined && onEnsureChildren
                              ? () => void onEnsureChildren(page.id)
                              : undefined
                          }
                          onRename={() => {}}
                          onTrash={() => onRecentTrash?.(page)}
                        />
                      );
                    })
                  )}
                </View>
              ) : null}
            </View>
          );
        }

        if (section.kind === 'favorites') {
          return (
            <View key={section.id} className="mb-1">
              <NavSectionHeader
                headerToggleOnly
                isActive={false}
                isOpen={isOpen}
                label={section.label}
                onOpen={() => toggleExpanded(section.id, true)}
                onToggle={() => toggleExpanded(section.id, true)}
              />
              {isOpen ? (
                <View>
                  {favoritePages.length === 0 ? (
                    <NavEmptyHint message="Star pages from the header or sidebar menu." />
                  ) : (
                    favoritePages.map((page) => {
                      const childList = childrenByParent[page.id];
                      const hasChildPages = pageHasChildPagesInTree(
                        page.id,
                        page.type,
                        childrenByParent,
                      );
                      return (
                        <SidebarItem
                          key={page.id}
                          blockId={page.id}
                          depth={1}
                          hasChildPages={hasChildPages}
                          isActive={activePageId === page.id}
                          label={page.title}
                          leadingIcon={page.type === 'database' ? 'database' : 'page'}
                          onDuplicate={() => onFavoriteDuplicate?.(page)}
                          onPress={() => onOpenPage(page.id, page.type)}
                          onPrefetch={
                            page.type === 'page' ? () => prefetchPageDocument(page.id) : undefined
                          }
                          onPrefetchChildren={
                            page.type === 'page' && childList === undefined && onEnsureChildren
                              ? () => void onEnsureChildren(page.id)
                              : undefined
                          }
                          onRename={() => onFavoriteRename?.(page)}
                          onTrash={() => onFavoriteTrash?.(page)}
                        />
                      );
                    })
                  )}
                </View>
              ) : null}
            </View>
          );
        }

        const rootId = section.rootBlockId;
        const headerToggleOnly = !rootId;

        return (
          <View key={section.id} className="mb-1">
            <NavSectionHeader
              headerToggleOnly={headerToggleOnly}
              isActive={rootId ? activePageId === rootId : false}
              isOpen={isOpen}
              label={section.label}
              onNewSubpage={
                rootId && onNewSubpageAtRoot ? () => onNewSubpageAtRoot(rootId) : undefined
              }
              onPrefetch={rootId ? () => prefetchPageDocument(rootId) : undefined}
              onOpen={
                headerToggleOnly
                  ? () => toggleExpanded(section.id, true)
                  : () => onOpenPage(rootId!, 'page')
              }
              onToggle={() => toggleExpanded(section.id, true)}
            />
            {isOpen ? (
              rootId ? (
                renderWorkspaceTree(rootId)
              ) : (
                <NavEmptyHint message="Workspace not ready. Sign out and sign in again to repair." />
              )
            ) : null}
          </View>
        );
      })}
    </>
  );
}
